import React from "react";
import ReactDOM from "react-dom/client";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./context/msalConfig";
import App from "./App";
import "./i18n";
import "./theme/global.css";
import { captureSignupCode } from "./services/onboardingApi";
import {
  isRecoverableRedirectFailure,
  shouldRetrySignIn,
  markSignInRetry,
  clearSignInRetry,
} from "./services/authRedirectRecovery";
import { loginRequest } from "./context/msalConfig";

// Before anything can redirect to sign-in: a ?code= on the landing link
// is gone once B2C sends them back, and the form that wants it does not
// exist until after they return (#222).
captureSignupCode();

const msalInstance = new PublicClientApplication(msalConfig);

function start() {
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });

  ReactDOM.createRoot(document.getElementById("root")).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </React.StrictMode>
  );
}

/*
 * THE REDIRECT IS FINISHED BEFORE ANYTHING RENDERS (#475).
 *
 * This used to await initialize() alone and render immediately. On the
 * way back from the identity provider that is too early: the response
 * is still sitting unprocessed in the URL, getAllAccounts() returns
 * NOTHING, and App.jsx renders <UnauthenticatedTemplate> -- the sign-in
 * screen -- for somebody who has just typed a correct password.
 *
 * Reported as having to click Sign in twice, reproducibly when
 * switching accounts: "entered info, took me back to Signin, clicking
 * Signin the second time took me directly into the app without
 * reentering the passcode". The second click works because by then the
 * response HAS been processed and the account is in the cache.
 *
 * MsalProvider does call handleRedirectPromise itself, but it does so
 * after mount -- and msal-react v2's UnauthenticatedTemplate does not
 * consider `inProgress`, so it renders the signed-out view while the
 * redirect is still being handled. Awaiting it here closes the window
 * entirely rather than narrowing it.
 *
 * NOTE FOR THE NEXT ATTEMPT, since this is the second: setting
 * navigateToLoginRequestUrl to false did NOT fix this. It is kept
 * because one fewer navigation is still one fewer thing to race, but
 * the cause was always the render happening too early.
 *
 * ── THIRD ATTEMPT, AND THE ORDERING WAS NOT THE WHOLE STORY ──
 *
 * The ordering above is right and is staying. It was not enough: the
 * device report that followed it carried the real error, thrown from
 * inside handleRedirectPromise rather than caused by anything racing it.
 *
 *     BrowserAuthError: no_cached_authority_error: No cached authority found.
 *
 * The catch below then rendered the app signed out, so somebody who had
 * typed a correct password was handed the sign-in button back -- the same
 * screen the ordering fix was chasing, reached a different way. That is
 * why two reasonable fixes in a row appeared to do nothing.
 *
 * The cause and its primary fix are in context/msalConfig.js
 * (storeAuthStateInCookie). The catch is no longer allowed to present a
 * failed handshake as a failed password; see below.
 */
msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then((response) => {
    // The account that just signed in, if this load IS a redirect
    // return. Otherwise fall back to whoever is already cached.
    const account = response?.account || msalInstance.getAllAccounts()[0];
    if (account) msalInstance.setActiveAccount(account);
    // A completed sign-in re-arms the one-shot recovery, so a failure
    // later in a long session still gets its retry.
    if (account) clearSignInRetry();
    start();
  })
  .catch((err) => {
    console.error("Auth redirect could not be completed", err);

    /*
     * FINISH THE SIGN-IN RATHER THAN ASK FOR IT AGAIN (#475).
     *
     * A lost-temporary-state failure is thrown AFTER the identity
     * provider has authenticated somebody -- which is exactly why
     * clicking Sign in a second time walks straight in without a
     * password. The upstream session is live; only our half of the
     * handshake failed. That second click is work the app can do
     * itself, and doing it is the difference between "I have to sign in
     * twice" and no symptom.
     *
     * Guarded to ONE attempt. A redirect loop back to the identity
     * provider is far worse than the bug being fixed, and the marker
     * lives in localStorage because sessionStorage is the storage whose
     * unreliability caused this.
     *
     * The dead hash goes first: its state matches nothing we hold, so
     * leaving it in the URL risks the retry tripping over the same
     * corpse. history.replaceState keeps this out of the back button.
     */
    if (isRecoverableRedirectFailure(err) && shouldRetrySignIn()) {
      markSignInRetry();
      try {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      } catch {
        /* a blocked history API must not stop the retry */
      }
      msalInstance.loginRedirect(loginRequest).catch((retryErr) => {
        /* If even the retry cannot start, fall back to the old
           behaviour rather than leaving a blank page. */
        console.error("Sign-in retry could not be started", retryErr);
        start();
      });
      return;
    }

    /* RENDER ANYWAY. A cancelled sign-in, a policy the user backed out
       of, a denied consent -- none of those are ours to retry, and
       sending them back to the identity provider would trap the user.
       A blank page is a worse failure than this one and much harder to
       report. The app comes up signed out, which is both true and
       recoverable. */
    start();
  });
